import { observer } from 'mobx-react-lite'
import { useFurnitureStore } from '@fabrick/components'
import { usePropertiesPanelStore } from '../stores/usePropertiesPanelStore'
import { fromSchema, Component } from '@fabrick/store'
import { v4 as uuid } from 'uuid'
import React, { useState, useEffect } from 'react'
import {
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Package,
  Square,
  Box,
  Puzzle,
  Trash2,
  Copy,
  FolderPlus
} from 'lucide-react'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { cn } from '@/lib/utils'

interface ComponentTreeItemProps {
  componentId: string
  depth: number
}

const ComponentTreeItem = observer(({ componentId, depth }: ComponentTreeItemProps) => {
  const furnitureStore = useFurnitureStore()
  const panelStore = usePropertiesPanelStore()
  const component = furnitureStore.components.get(componentId)
  const iconSize = 16
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)

  if (!component) return null

  const childComponents = component.children ? Object.values(component.children) : []
  const hasChildren = childComponents.length > 0
  const isSelected = furnitureStore.selectedComponentIds.has(componentId)
  const isExpanded = panelStore.isComponentExpanded(componentId)
  
  // Memoize available groups to prevent infinite loops
  const availableGroups = React.useMemo(() => {
    if (component.parent) return []
    return Array.from(furnitureStore.components.values())
      .filter((c): c is Component & { type: 'group' } => {
        return c != null && typeof c === 'object' && 'type' in c && c.type === 'group' && 'id' in c && c.id !== componentId
      })
  }, [furnitureStore.components.size, componentId, component.parent])
  const isHidden = (() => {
    let current: typeof component | null = component
    while (current) {
      if (current.layout.hidden) {
        return true
      }
      current = current.parent
    }
    return false
  })()

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation()
    panelStore.toggleComponentExpansion(componentId)
  }

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation()
    panelStore.toggleComponentVisibility(componentId)
  }

  const handleSelect = (e: React.MouseEvent) => {
    const shiftKey = e.shiftKey || e.metaKey || e.ctrlKey
    panelStore.selectComponent(componentId, shiftKey)
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Select component if not already selected
    if (!isSelected) {
      panelStore.selectComponent(componentId, false)
    }
    setContextMenu({ x: e.clientX, y: e.clientY })
  }

  const handleDelete = () => {
    furnitureStore.removeComponent(componentId)
    setContextMenu(null)
  }

  const handleDuplicate = () => {
    furnitureStore.duplicateComponent(componentId)
    setContextMenu(null)
  }

  const handleMenuToggleVisibility = () => {
    panelStore.toggleComponentVisibility(componentId)
    setContextMenu(null)
  }

  const handleMoveToGroup = (groupId: string) => {
    furnitureStore.moveComponentToGroup(componentId, groupId)
    setContextMenu(null)
  }

  const handleRemoveFromGroup = () => {
    furnitureStore.removeComponentFromGroup(componentId)
    setContextMenu(null)
  }

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => {
      if (contextMenu) {
        setContextMenu(null)
      }
    }
    if (contextMenu) {
      window.addEventListener('click', handleClick)
      return () => window.removeEventListener('click', handleClick)
    }
  }, [contextMenu])

  return (
    <div className="flex flex-col relative" style={{ paddingLeft: `${depth * 16}px` }}>
      {contextMenu && (
        <div
          className="fixed z-50 w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{ 
            left: contextMenu.x, 
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="px-2 py-1.5 text-sm font-semibold text-foreground">
            {component.metadata?.name || component.type}
          </div>
          <div className="-mx-1 my-1 h-px bg-border" />
          
          <div 
            className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            onClick={handleDuplicate}
          >
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
            <span className="ml-auto text-xs tracking-widest text-muted-foreground">Ctrl+D</span>
          </div>
          
          <div 
            className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
            onClick={handleMenuToggleVisibility}
          >
            {isHidden ? (
              <>
                <Eye className="mr-2 h-4 w-4" />
                Show
              </>
            ) : (
              <>
                <EyeOff className="mr-2 h-4 w-4" />
                Hide
              </>
            )}
          </div>
          
          {/* Move to Group option - only show if component is not already in a group and there are groups available */}
          {!component.parent && availableGroups.length > 0 && (
            <>
              <div className="-mx-1 my-1 h-px bg-border" />
              <div className="px-2 py-1.5 text-xs text-muted-foreground">Move to Group</div>
              {availableGroups.slice(0, 5).map(group => (
                <div
                  key={group.id}
                  className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 pl-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleMoveToGroup(group.id)
                  }}
                >
                  <FolderPlus className="mr-2 h-4 w-4" />
                  {group.name || 'Unnamed Group'}
                </div>
              ))}
            </>
          )}
          
          {/* Remove from Group option - only show if component is in a group */}
          {component.parent && (
            <>
              <div className="-mx-1 my-1 h-px bg-border" />
              <div 
                className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={handleRemoveFromGroup}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Remove from Group
              </div>
            </>
          )}
          
          <div className="-mx-1 my-1 h-px bg-border" />
          
          <div 
            className="relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm text-destructive outline-none hover:bg-accent hover:text-destructive"
            onClick={handleDelete}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
            <span className="ml-auto text-xs tracking-widest text-muted-foreground">Del</span>
          </div>
        </div>
      )}
      <div
        className={cn(
          "flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer transition-colors select-none",
          "hover:bg-accent",
          isSelected && "bg-accent border-l-2 border-primary pl-1.5",
          isHidden && "opacity-70"
        )}
        onClick={handleSelect}
        onContextMenu={handleContextMenu}
        title={isSelected ? "Selected (Shift+Click to add/remove)" : "Click to select (Shift+Click to multi-select)"}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "h-[18px] w-[18px] p-0 text-muted-foreground hover:text-foreground",
            !hasChildren && "invisible"
          )}
          onClick={handleToggleExpand}
          disabled={!hasChildren}
          aria-label={isExpanded ? 'Collapse children' : 'Expand children'}
        >
          {hasChildren ? (isExpanded ? <ChevronDown size={iconSize} /> : <ChevronRight size={iconSize} />) : <span className="w-4 h-4" />}
        </Button>

        <span className="flex items-center justify-center w-5 h-5 text-muted-foreground flex-shrink-0">
          {component.type === 'panel' ? <Square size={iconSize} /> :
           component.type === 'group' ? <Package size={iconSize} /> :
           component.type === 'lumber' ? <Box size={iconSize} /> : <Puzzle size={iconSize} />}
        </span>

        <span className="flex-1 text-sm truncate">
          {component.name || component.metadata?.name || component.type}
          {component.type === 'group' && component.name && (
            <span className="ml-1 text-xs text-muted-foreground">(Group)</span>
          )}
        </span>

        {isHidden && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 h-5 uppercase">
            Hidden
          </Badge>
        )}

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "h-[26px] w-[26px] p-0 text-muted-foreground hover:text-foreground opacity-85 hover:opacity-100",
            isHidden && "opacity-100 text-primary"
          )}
          onClick={handleToggleVisibility}
          title={isHidden ? 'Show component' : 'Hide component'}
          aria-label={isHidden ? 'Show component' : 'Hide component'}
        >
          {isHidden ? <Eye size={iconSize} /> : <EyeOff size={iconSize} />}
        </Button>
      </div>

      {hasChildren && isExpanded && (
        <div className="flex flex-col gap-0.5">
          {childComponents.map((child: any) => (
            <ComponentTreeItem
              key={child.id}
              componentId={child.id}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
})

export const ComponentsTree = observer(() => {
  const panelStore = usePropertiesPanelStore()
  const furnitureStore = useFurnitureStore()
  const rootComponents = panelStore.rootComponents
  const selectedCount = furnitureStore.selectedComponentIds.size
  const hasSelection = selectedCount > 0
  
  // Ref to prevent multiple simultaneous group creations
  const isGroupingRef = React.useRef(false)

  const handleCreateGroup = React.useCallback(() => {
    if (isGroupingRef.current) return
    isGroupingRef.current = true
    
    try {
      const position: [number, number, number] = [0, 0.5, 0]
      const schema = {
        id: uuid(),
        type: 'group' as const,
        name: 'New Group',
        layout: {
          transform: {
            position: { x: position[0], y: position[1], z: position[2] },
            rotation: { x: 0, y: 0, z: 0, w: 1 },
            scale: { x: 1, y: 1, z: 1 }
          }
        },
        children: {}
      }
      const component = fromSchema(schema)
      furnitureStore.addComponent(component)
      furnitureStore.selectComponent(component.id, false)
    } finally {
      // Reset after a small delay to allow MobX updates to complete
      setTimeout(() => {
        isGroupingRef.current = false
      }, 100)
    }
  }, [furnitureStore])

  const handleGroupSelection = React.useCallback(() => {
    if (selectedCount === 0 || isGroupingRef.current) return
    
    isGroupingRef.current = true
    
    try {
      // Prevent multiple calls by checking if we're already processing
      const selectedIds = Array.from(furnitureStore.selectedComponentIds)
      
      // Don't group if any selected item is already a group or in a group
      const canGroup = selectedIds.every(id => {
        const comp = furnitureStore.components.get(id)
        return comp && comp.type !== 'group' && !comp.parent
      })
      
      if (!canGroup) return
      
      // Calculate center position of all selected components
      let centerX = 0, centerY = 0, centerZ = 0
      let validCount = 0
      
      selectedIds.forEach(componentId => {
        const comp = furnitureStore.components.get(componentId)
        if (comp && (comp.layout?.transform || comp.transform)) {
          const transform = comp.layout?.transform || comp.transform
          centerX += transform.position.x
          centerY += transform.position.y
          centerZ += transform.position.z
          validCount++
        }
      })
      
      if (validCount > 0) {
        centerX /= validCount
        centerY /= validCount
        centerZ /= validCount
      }
      
      // Create a new group at the center position
      const schema = {
        id: uuid(),
        type: 'group' as const,
        name: 'Grouped Items',
        layout: {
          transform: {
            position: { x: centerX, y: centerY, z: centerZ },
            rotation: { x: 0, y: 0, z: 0, w: 1 },
            scale: { x: 1, y: 1, z: 1 }
          }
        },
        children: {}
      }
      const group = fromSchema(schema)
      furnitureStore.addComponent(group)
      
      // Move all selected components to the group
      // moveComponentToGroup will handle position conversion automatically
      selectedIds.forEach((componentId, index) => {
        furnitureStore.moveComponentToGroup(componentId, group.id, `item${index}`)
      })
      
      // Select the new group
      furnitureStore.selectComponent(group.id, false)
    } finally {
      // Reset after a small delay to allow MobX updates to complete
      setTimeout(() => {
        isGroupingRef.current = false
      }, 100)
    }
  }, [selectedCount, furnitureStore])

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Components</h3>
        <div className="flex gap-1">
          {hasSelection && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={handleGroupSelection}
              title="Group selected components"
            >
              <FolderPlus className="h-3 w-3 mr-1" />
              Group ({selectedCount})
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={handleCreateGroup}
            title="Create new group"
          >
            <Package className="h-3 w-3 mr-1" />
            New Group
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-0.5">
        {!rootComponents || rootComponents.length === 0 ? (
          <div className="py-5 text-center text-xs text-muted-foreground">No components in scene</div>
        ) : (
          rootComponents.map(component => (
            <ComponentTreeItem
              key={component.id}
              componentId={component.id}
              depth={0}
            />
          ))
        )}
      </div>
    </div>
  )
})

